import React, { useState } from "react";
import { Box, Button, Switch, FormControlLabel } from "@material-ui/core";
import { CTextField, CSelect, HFlex, ProgressButton, Text, DialogSuccessNotice, useErrorContext } from "shared";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateHcp, CreateHcpInput } from "api/hooks/useHCPs";
import { useHealthCareOrganizations } from "routes/healthCareOrganizations/queries/useHealthCareOrganizations";

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone_number: string;
  address: string;
  iana_timezone: string;
  category: string;
  admits_patients: boolean;
  invitation_payer_id: string;
  health_care_organization_id: string;
}

const timezoneOptions = [
  { value: "America/New_York", label: "America/New_York" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Denver", label: "America/Denver" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles" },
  { value: "America/Phoenix", label: "America/Phoenix" },
  { value: "America/Anchorage", label: "America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
];

const categoryOptions = [
  { value: "0", label: "Joint Academy" },
  { value: "1", label: "Private Sector" },
  { value: "2", label: "Public Sector" },
];

const CreateHcpForm: React.VFC<Props> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const [successData, setSuccessData] = useState<{ heading: string; body: string } | null>(null);

  const hcosQuery = useHealthCareOrganizations();
  const hcos = hcosQuery.data ?? [];
  const hcoOptions = [{ value: "", label: "None" }, ...hcos.map((h) => ({ value: String(h.id), label: h.name }))];

  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      email: "",
      phone_number: "",
      address: "",
      iana_timezone: "",
      category: "1",
      admits_patients: false,
      invitation_payer_id: "",
      health_care_organization_id: "",
    },
  });

  const createMutation = useCreateHcp();

  const submit = form.handleSubmit(async (fields) => {
    try {
      const input: CreateHcpInput = {
        name: fields.name,
        address: fields.address,
        iana_timezone: fields.iana_timezone,
        email: fields.email || undefined,
        phone_number: fields.phone_number || undefined,
        category: Number(fields.category),
        admits_patients: fields.admits_patients,
        invitation_payer_id: fields.invitation_payer_id || undefined,
        health_care_organization_id: fields.health_care_organization_id
          ? Number(fields.health_care_organization_id)
          : undefined,
      };
      const created = await createMutation.mutateAsync(input);
      setSuccessData({
        heading: "HCP created successfully",
        body: `${created.name} (ID: ${created.id})`,
      });
    } catch (err) {
      setError("Could not create health care provider", err);
    }
  });

  if (successData)
    return <DialogSuccessNotice heading={successData.heading} body={successData.body} onClose={() => onSuccess()} />;

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        Create Health Care Provider
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField required name="name" autoFocus label={t("form.name")} margin="normal" size="small" fullWidth />

          <CTextField name="email" label="Email" type="email" margin="normal" size="small" fullWidth />

          <CTextField name="phone_number" label="Phone Number" margin="normal" size="small" fullWidth />

          <CTextField required name="address" label="Address" margin="normal" size="small" fullWidth />

          <CSelect
            required
            name="iana_timezone"
            label="Timezone"
            items={timezoneOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CSelect
            name="category"
            label="Category"
            items={categoryOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <Controller
            name="admits_patients"
            control={form.control}
            render={({ value, onChange }) => (
              <FormControlLabel
                control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} color="primary" />}
                label="Admits Patients"
              />
            )}
          />

          <CTextField
            name="invitation_payer_id"
            label="Invitation Payer ID"
            margin="normal"
            size="small"
            fullWidth
          />

          <CSelect
            name="health_care_organization_id"
            label="Health Care Organization"
            items={hcoOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button disabled={createMutation.isLoading} onClick={onCancel}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={createMutation.isLoading}>{t("buttons.create")}</ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CreateHcpForm;
